import { expressQuerySelectorAll, expressQuerySelector, htmlToElement, expressEventListener } from "../../common/html";
import { compositeDisposable } from "../../utils/disposable";
import { getHtmlRequest } from "../../utils/fetch";
import { isTouchDevice } from "../../common/device";
import { INavigationDesktopDeps } from "./navigation-desktop-first-level";

export interface INavigationDesktopThirdLevelSideContainerDeps {
	imageUrl: string;
	title: string;
	link: string;
}
export function CreateNavigationDesktopThirdLevel(container: HTMLElement, deps: INavigationDesktopDeps) {
	const { getActiveIndex } = deps;
	const HIDE_CLASS = "u-hide";
	const CONTAINER_HIDE_CLASS = "o-navigation-desktop__third-level--hidden";
	const LOADING_CLASS = "skeleton-loading";
	const loading = {}; // save the first and second level index with there loading sate
	const thirdLevelGrid = expressQuerySelector<HTMLElement>(container, ".technical-navigation-third-level-grid");
	let menuItems = expressQuerySelectorAll<HTMLElement>(thirdLevelGrid, ".technical-navigation-item");
	const gridItems = expressQuerySelectorAll<HTMLElement>(thirdLevelGrid, ".technical-navigation-grid-item");
	const sideContainer = expressQuerySelector<HTMLElement>(container, ".technical-navigation-third-level-side");

	const addEventListenersToMenuItem = (x: HTMLElement) => {
		const itemAction = expressQuerySelector(x, ".technical-navigation-third-level-side-link", false) || x;
		return compositeDisposable([
			expressEventListener(itemAction, "mouseover", () => {
				!isTouchDevice() &&
					updateSideContainer(
						{
							imageUrl: x.dataset.imageUrl,
							title: x.dataset.name,
							link: x.dataset.link,
						},
						false,
					);
			}),
			expressEventListener(itemAction, "mouseleave", () => {
				updateSideContainer(
					{
						imageUrl: sideContainer.dataset.imageUrl,
						title: sideContainer.dataset.title,
						link: sideContainer.dataset.link,
					},
					true,
				);
			}),
		]);
	};
	const disposible = gridItems.map((x) => addEventListenersToMenuItem(x));

	const open = async (
		index: string,
		sideContainerDeps?: INavigationDesktopThirdLevelSideContainerDeps,
	): Promise<boolean> => {
		const menuLevelParts = index.split(".");
		const firstLevelIndex = menuLevelParts[0];
		const secondLevelActiveIndex = menuLevelParts[1];
		const thirdMenuAlreadyLoaded = menuItems.some((x) => x.dataset.parentIndex === index);

		const loadingKey = index;
		if (!thirdMenuAlreadyLoaded && !loading[loadingKey]) {
			loading[loadingKey] = true;
			container.classList.toggle(CONTAINER_HIDE_CLASS, false);
			thirdLevelGrid.classList.toggle(HIDE_CLASS, false);
			sideContainer.classList.toggle(HIDE_CLASS, false);
			container.classList.toggle(LOADING_CLASS, true);

			const request = await getHtmlRequest(
				`apicore/headerapi/desktopmenu?indexLevel1=${firstLevelIndex}&indexLevel2=${secondLevelActiveIndex}`,
			);

			const requestNode = htmlToElement(request) as HTMLElement;
			const thirdLevelItems = expressQuerySelectorAll<HTMLElement>(
				requestNode,
				`.technical-navigation-item.m-navigation-desktop-third-level__grid-item[data-parent-index='${index}']`,
			);

			const thirdLevelGridItems = expressQuerySelectorAll<HTMLElement>(requestNode, ".technical-navigation-grid-item");
			// add items to DOM && javascript variable
			thirdLevelGridItems.forEach((x) => disposible.push(addEventListenersToMenuItem(x)));
			const isStillActiveIndex = index === getActiveIndex();
			thirdLevelItems.forEach((x) => {
				x.classList.toggle(HIDE_CLASS, !isStillActiveIndex);
				thirdLevelGrid.appendChild(x);
			});
			menuItems = [...menuItems, ...thirdLevelItems];
			loading[loadingKey] = false;

			if (!isStillActiveIndex) return false; // don't update item not active anymore
		}
		menuItems.forEach((x) => x.classList.toggle(HIDE_CLASS, x.dataset.parentIndex !== index));
		container.classList.toggle(LOADING_CLASS, !!loading[loadingKey]);
		container.classList.toggle(CONTAINER_HIDE_CLASS, false);
		thirdLevelGrid.classList.toggle(HIDE_CLASS, false);
		sideContainer.classList.toggle(HIDE_CLASS, false);

		// set defaults for mouseleave on item
		if (sideContainerDeps) {
			sideContainer.dataset.imageUrl = sideContainerDeps.imageUrl;
			sideContainer.dataset.title = sideContainerDeps.title;
			sideContainer.dataset.link = sideContainerDeps.link;
		}

		sideContainerDeps && updateSideContainer(sideContainerDeps);

		return true;
	};

	const updateSideContainer = (settings: INavigationDesktopThirdLevelSideContainerDeps, useTitleFormat = true) => {
		const { imageUrl, title, link } = settings;
		const sideContainerImage = expressQuerySelector<HTMLImageElement>(
			sideContainer,
			".technical-navigation-third-level-side-image",
		);
		const sideContainerImageSideLink = expressQuerySelector<HTMLImageElement>(
			sideContainer,
			".technical-navigation-third-level-image-side-link.technical-navigation-third-level-side-link",
		);
		const sideContainerTitle = expressQuerySelector<HTMLElement>(
			sideContainer,
			".technical-navigation-third-level-side-title",
		);
		const sideContainerLinks = expressQuerySelectorAll<HTMLAnchorElement>(
			sideContainer,
			".technical-navigation-third-level-side-link",
		);

		sideContainerImageSideLink.classList.toggle(HIDE_CLASS, !imageUrl);
		if (imageUrl) {
			sideContainerImage.src = imageUrl;
		} else {
			sideContainerImage.removeAttribute("src");
		}
		sideContainerTitle.textContent = useTitleFormat ? sideContainer.dataset.format.replace("{0}", title) : title;
		sideContainerLinks.forEach((x) => (x.href = link));
	};

	const close = () => {
		thirdLevelGrid.classList.toggle(HIDE_CLASS, true);
		sideContainer.classList.toggle(HIDE_CLASS, true);
		container.classList.toggle(CONTAINER_HIDE_CLASS, true);
	};

	return {
		open,
		close,
		dispose: () => disposible.forEach((x) => x.dispose()),
	};
}
